import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import { FCC } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import {
  useSession,
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { InterstitialsContext } from './InterstitialsContext';
import { UserRegistrationInterstitial } from './interstitials/UserRegistrationInterstitial';
import { VideoWalkthroughInterstitial } from './interstitials/VideoWalkthroughInterstitial';
import { getUserFlowCompletionsQuery } from './operations';
import {
  BaseInterstitial,
  InterstitialContext,
  InterstitialsContextType,
} from './types';

// Static list of available interstitials, sorted by priority (highest first)
export const availableInterstitials = [
  UserRegistrationInterstitial,
  VideoWalkthroughInterstitial,
].sort((a, b) => b.priority - a.priority);

export const InterstitialsProvider: FCC = ({ children }) => {
  const logger = useNamedLogger('InterstitialsProvider');

  const tenantId = useTenantIdFromParams() as string;
  const session = useSession();
  const userId = useUserIdFromSession();
  const location = useLocation();
  const apiClient = useApiClient();

  const { data, loading: completionsLoading } = useQuery(
    getUserFlowCompletionsQuery,
    {
      skip: !userId || !tenantId,
    },
  );

  const [userRegistrationShouldShow, setUserRegistrationShouldShow] = useState<
    boolean | null
  >(null);
  const [
    userRegistrationShouldShowLoading,
    setUserRegistrationShouldShowLoading,
  ] = useState<boolean>(true);

  useEffect(() => {
    if (!userId || !tenantId) {
      setUserRegistrationShouldShow(null);
      setUserRegistrationShouldShowLoading(false);
      return;
    }

    let cancelled = false;
    setUserRegistrationShouldShowLoading(true);

    apiClient.interstitials
      .getUserRegistrationShouldShow({ params: { tenantId } })
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (response.status === 200) {
          setUserRegistrationShouldShow(response.body.shouldShow);
        } else {
          logger.warn('Failed to fetch user-registration should-show', {
            status: response.status,
          });
          setUserRegistrationShouldShow(false);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        logger.warn('Error fetching user-registration should-show', { error });
        setUserRegistrationShouldShow(false);
      })
      .finally(() => {
        if (!cancelled) {
          setUserRegistrationShouldShowLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiClient, userId, tenantId, logger]);

  const interstitialContext = useMemo<InterstitialContext>(
    () => ({
      userId,
      tenantId,
      data: data || { userInterstitialFlows: [] },
      userRegistrationShouldShow,
      currentPath: location.pathname,
      userSession: session,
      logger,
    }),
    [
      userId,
      tenantId,
      data,
      userRegistrationShouldShow,
      location.pathname,
      session,
      logger,
    ],
  );

  const shouldShowInterstitial = useCallback(
    (interstitial: BaseInterstitial, context: InterstitialContext): boolean => {
      // Check path matching first (fast check)
      if (interstitial.showOnPaths && interstitial.showOnPaths.length > 0) {
        const matchesPath = interstitial.showOnPaths.some((pathRegex) =>
          context.currentPath?.match(pathRegex),
        );
        if (!matchesPath) {
          return false;
        }
      }

      // Run the custom logic
      return interstitial.shouldShow(context);
    },
    [],
  );

  const isLoading = completionsLoading || userRegistrationShouldShowLoading;

  const activeInterstitial = useMemo(() => {
    if (isLoading || !userId) {
      return null;
    }

    const active =
      availableInterstitials.find((interstitial) =>
        shouldShowInterstitial(interstitial, interstitialContext),
      ) || null;

    if (active) {
      logger.info('Showing interstitial', { name: active.name });
    }

    return active;
  }, [interstitialContext, isLoading, userId, shouldShowInterstitial, logger]);

  const contextValue = useMemo<InterstitialsContextType>(
    () => ({
      activeInterstitial,
      isLoading,
      context: isLoading || !userId ? null : interstitialContext,
      setUserRegistrationShouldShow,
    }),
    [activeInterstitial, isLoading, userId, interstitialContext],
  );

  return (
    <InterstitialsContext.Provider value={contextValue}>
      {children}
    </InterstitialsContext.Provider>
  );
};
